/**
 * Pharma sync-interval conversion (PUT
 * `/api/v1/companies/{company_id}/integration` contract): the API carries
 * intervals in seconds (strings like `"10800"`), while the form lets the
 * admin pick hours (minimum 2) and converts on the boundary.
 */

export const SYNC_INTERVAL_KEY_SYNC_SHOPS_INTERVAL =
  'SYNC_INTERVAL_KEY_SYNC_SHOPS_INTERVAL' as const;
export const SYNC_INTERVAL_KEY_SYNC_PRODUCTS_INTERVAL =
  'SYNC_INTERVAL_KEY_SYNC_PRODUCTS_INTERVAL' as const;

export const SECONDS_PER_HOUR = 3600;
export const MIN_SYNC_INTERVAL_HOURS = 2;

/** Defaults mirror the API doc example (3h shops, 4h products). */
export const DEFAULT_SYNC_INTERVAL_HOURS = {
  [SYNC_INTERVAL_KEY_SYNC_SHOPS_INTERVAL]: 3,
  [SYNC_INTERVAL_KEY_SYNC_PRODUCTS_INTERVAL]: 4,
} as const;

/** Hours picked in the form → whole seconds for the payload. */
export function pharmaHoursToSeconds(hours: number): string {
  return String(Math.max(0, Math.round(hours * SECONDS_PER_HOUR)));
}

/** Seconds from the API (string or number) → hours for the form. */
export function pharmaSecondsToHours(
  value: string | number | undefined,
  fallbackHours: number,
): number {
  const seconds = typeof value === 'number' ? value : Number(value ?? NaN);
  if (!Number.isFinite(seconds) || seconds <= 0) return fallbackHours;
  return seconds / SECONDS_PER_HOUR;
}

export const MAX_SYNC_INTERVAL_HOURS = 8;

/** Hours the form offers — the single source for the schema enum. */
export const SYNC_INTERVAL_HOUR_OPTIONS = ['2', '3', '4', '5', '6', '7', '8'] as const;
export type SyncIntervalHour = (typeof SYNC_INTERVAL_HOUR_OPTIONS)[number];

/**
 * Seconds from the API → a selectable hour option ('2'–'8'): rounds and
 * clamps into the range the form offers, so a stored odd value still lands
 * on a valid select option.
 */
export function pharmaSecondsToHourOption(
  value: string | number | undefined,
  fallbackHours: number,
): SyncIntervalHour {
  const hours = pharmaSecondsToHours(value, fallbackHours);
  const clamped = Math.min(
    MAX_SYNC_INTERVAL_HOURS,
    Math.max(MIN_SYNC_INTERVAL_HOURS, Math.round(hours)),
  );
  return String(clamped) as SyncIntervalHour;
}
